import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { getAuthToken } from '../lib/auth';

// default
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// Set initial token if available
const initialToken = getAuthToken();
if (initialToken)
  axios.defaults.headers.common["Authorization"] = "Bearer " + initialToken;

// Flag para prevenir múltiples redirecciones simultáneas
let isRedirecting = false;

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const status = error.response?.status;
    
    // Manejar específicamente errores 401 (Unauthorized)
    if (status === 403) {
      if (!isRedirecting) {
        isRedirecting = true;
        
        try {
          // Limpiar sessionStorage
          sessionStorage.removeItem("authUser");
          
          // Redirigir a login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } catch (err) {
          console.error('Error durante logout automático:', err);
        }
        
        // Resetear el flag después de un tiempo
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }
      
      return Promise.reject('Sesión expirada');
    }
    
    // Manejar otros errores normalmente
    let message;
    switch (status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      case 400:
        message = error.response?.data || "Bad Request";
        break;
      default:
        message =
          error.response?.data?.message ||
          error.message ||
          "Unexpected error";
    }
    return Promise.reject(message);
  }
);
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Fetches data from the given URL
   */
  get = (url: string, params?: any): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * Posts the given data to the URL
   */
  create = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.post(url, data);
  };

  /**
   * Descarga archivos (como Excel) desde el servidor
   */
  download = (url: string, data: any): Promise<AxiosResponse<Blob>> => {
    return axios.post(url, data, {
      responseType: 'blob',
    });
  };


  /**
   * Updates data
   */
  update = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

export { APIClient, setAuthorization };
